import { Fragment, ReactNode } from "react";

import { Button, Grid2, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { ERole } from "../../../@types";
import { useAccount } from "../../../hooks/useAccount";
import useScreenSize from "../../../hooks/useScreenSize";
import { useWhiteScreen } from "../../../hooks/useWhiteScreen";
import { Container } from "../../common/Container/Container";
import {
	AutomatePaymentsIcon,
	BlueCheckIcon,
	BuildCoursesIcon,
	CentralizedMessagingIcon,
	PersonalSchedulerIcon,
	SetYourCalendarIcon,
	TrainingHubIcon,
} from "./components/OrkaSoftwareIcons";

import "./orka_software.scss";

export type THowItWorksData = {
	icon: ReactNode;
	title: string;
	content: string;
};

export type THowItWorksSection = {
	title: string;
	data: THowItWorksData[];
};

const howItWorksSections: THowItWorksSection[] = [
	{
		title: "Create & Share",
		data: [
			{
				icon: <BuildCoursesIcon />,
				title: "Build",
				content:
					"Build in-person, online, or hybrid services with a framework that lets you showcase your unique training and include files and videos of any size to enrich your classes.",
			},
			{
				icon: <TrainingHubIcon />,
				title: "Visibility",
				content:
					"Allow our AI-powered search engine to match you with relevant students while you focus on teaching and networking.",
			},
		],
	},
	{
		title: "Takes care of the admin",
		data: [
			{
				icon: <SetYourCalendarIcon />,
				title: "Set your calendar",
				content:
					"Control your availability and prevent overbooking, customize exactly when students can book, block off personal time, set breaks between appointments, and link to Google Calendar.",
			},
			{
				icon: <PersonalSchedulerIcon />,
				title: "Personal scheduler",
				content:
					"Increase student commitment, eliminate back-and-forth communication to find mutually suitable time slots, and automatically have appointments entered into your calendar.",
			},
			{
				icon: <AutomatePaymentsIcon />,
				title: "Automate payments",
				content:
					"Stay professional and friendly. You're a trainer, not a debt collector. Get automatic deposits into your bank account and easily access your earnings.",
			},
			{
				icon: <CentralizedMessagingIcon />,
				title: "Centralized Messaging",
				content:
					"Dedicated space to stay in touch with your students and communicate with prospective candidates, helping you stay organized. ",
			},
		],
	},
];

export function OrkaSoftware() {
	useWhiteScreen();
	const { width } = useScreenSize();
	const isMobile = width <= 768;
	const isPhone = width <= 576;
	const isNotBigScreen = width <= 1200;

	const navigate = useNavigate();
	const { account } = useAccount();

	const onClickGetStarted = () => {
		if (account) {
			navigate("/training/dashboard");
		} else {
			navigate("/sign-up", { state: { role: ERole.INSTRUCTOR } });
		}
	};

	const getHowItWorksSpacing = () => {
		if (!isMobile) {
			return "48px";
		}
		if (isPhone) {
			return "24px";
		}
		return "36px";
	};

	return (
		<Container
			className="orka-software-container"
			navbarOrkaProps={{ white: true }}>
			<div className="orka-software-content-container">
				{!isMobile && (
					<div className="for-instructor-image-container">
						<img
							src={require("../../imagenes/landing_page_background_desktop.png")}
							className="image"
						/>
						<div className="caption-container">
							<Typography className="title bold">
								Designed for Instructors
							</Typography>
							<Typography className="description">
								Technology to start and run your own teaching business
							</Typography>
							<Button
								className="get-started-button"
								onClick={onClickGetStarted}>
								Get Started
							</Button>
						</div>
					</div>
				)}
				{isMobile && (
					<div className="for-instructor-image-container">
						<img
							src={require("../../imagenes/landing_page_background_mobile.png")}
							className="image"
						/>
						<div className="caption-container">
							<Typography className="title bold">
								Designed for
								<br />
								Instructors
							</Typography>
							<Typography className="description">
								Technology to start and run your own
								<br />
								teaching business
							</Typography>
							<Button
								className="get-started-button"
								onClick={onClickGetStarted}>
								Get Started
							</Button>
						</div>
					</div>
				)}
				<Grid2 container className="introduction-container" spacing={4}>
					<Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
						<div className="introduction-info-container">
							<Typography className="big-text blue-title">PERSONAL</Typography>
							<Typography className="title-text bold">
								Teach your way
							</Typography>
							<Typography className="normal-text">
								Instead of relying on stocked libraries of courses that don't
								match your style and expertise, share what inspires and moves
								you.
							</Typography>
							<div>
								<div className="item">
									<BlueCheckIcon />
									<Typography className="normal-text bold content">
										Host and share content
									</Typography>
								</div>
								<div className="item">
									<BlueCheckIcon />
									<Typography className="normal-text bold content">
										Your style
									</Typography>
								</div>
								<div className="item">
									<BlueCheckIcon />
									<Typography className="normal-text bold content">
										Stay organized
									</Typography>
								</div>
							</div>
						</div>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
						<div className="blue-video-container">
							<div className="video-container">
								<iframe
									src="https://player.vimeo.com/video/963916900?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
									style={{ width: "100%", height: "100%" }}
									title="Create and share"
								/>
							</div>
						</div>
					</Grid2>
					{!isNotBigScreen && (
						<Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
							<div className="yellow-video-container">
								<div className="video-container">
									<iframe
										src="https://player.vimeo.com/video/964089497?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
										style={{ width: "100%", height: "100%" }}
										title="Payment"
									/>
								</div>
							</div>
						</Grid2>
					)}
					<Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
						<div className="introduction-info-container">
							<Typography className="big-text yellow-title">
								AUTOMATION
							</Typography>
							<Typography className="title-text bold">Save energy</Typography>
							<Typography className="normal-text">
								You are worth it. Instead of handling payment collections,
								manual appointment entries, and working around scheduling
								software, integrate banking and scheduling with your courses in
								just a few clicks.
							</Typography>
							<div>
								<div className="item">
									<BlueCheckIcon />
									<Typography className="normal-text bold content">
										Elevate your professional image
									</Typography>
								</div>
								<div className="item">
									<BlueCheckIcon />
									<Typography className="normal-text bold content">
										No billing or uncomfortable 'pay me' moments
									</Typography>
								</div>
								<div className="item">
									<BlueCheckIcon />
									<Typography className="normal-text bold content">
										Multiple payment options with no processing fees
									</Typography>
								</div>
							</div>
						</div>
					</Grid2>
					{isNotBigScreen && (
						<Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
							<div className="yellow-video-container">
								<div className="video-container">
									<iframe
										src="https://player.vimeo.com/video/964089497?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
										style={{ width: "100%", height: "100%" }}
										title="Payment"
									/>
								</div>
							</div>
						</Grid2>
					)}
				</Grid2>

				<div className="how-it-works-container">
					<Typography className="big-text title">HOW IT WORKS?</Typography>
					{howItWorksSections.map((section) => {
						return (
							<Fragment key={section.title}>
								<Typography className="title-text subtitle">
									{section.title}
								</Typography>
								<div className="list-item-container">
									<Grid2 container spacing={getHowItWorksSpacing()}>
										{section.data.map((item) => {
											return (
												<Grid2
													size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
													key={item.title}>
													<div className="item">
														{item.icon}
														<Typography className="big-text item-title">
															{item.title}
														</Typography>
														<Typography className="normal-text item-content">
															{item.content}
														</Typography>
													</div>
												</Grid2>
											);
										})}
									</Grid2>
								</div>
							</Fragment>
						);
					})}
				</div>

				<div className="are-you-ready-container">
					<div className="are-you-ready-content-container">
						<Typography className="title-text bold title">
							Ready to have it all-in-one place?
						</Typography>
						<Typography className="normal-text content">
							Unlock your creativity and build streams of income while software
							takes care of the admin.
						</Typography>
						<Button
							className="normal-button start-building-button"
							onClick={onClickGetStarted}>
							Start building
						</Button>
					</div>
				</div>

				<div className="for-instructor-note-container">
					<div className="for-instructor-note">
						<Typography className="title-text bold">
							We've got you covered!
						</Typography>
						<Typography className="big-text">
							We designed these learning, scheduling, and business management
							systems while obsessing about you, and would love to hear from
							you.
						</Typography>
					</div>
					<img
						className="ceo-image"
						src={require("../../imagenes/our_ceo.png")}
					/>
				</div>
			</div>
		</Container>
	);
}
